import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Calendar, Clock, ExternalLink } from "lucide-react";
import { useConsultation } from "../components/ConsultationContext";
import SafeImage from "../components/SafeImage";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const consult = useConsultation();

  React.useEffect(() => {
    setPost(null); setNotFound(false);
    const cancelled = { current: false };
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      if (cancelled.current) return;
      ctrl.abort();
      setNotFound(true);
    }, 8000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogger/post/${slug}`, { signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => {
        if (cancelled.current) return;
        setPost(d);
        clearTimeout(t);
      })
      .catch(() => {
        // Ignore aborts caused by effect cleanup (StrictMode double-mount, slug change).
        if (cancelled.current) return;
        setNotFound(true);
        clearTimeout(t);
      });
    return () => {
      cancelled.current = true;
      clearTimeout(t);
      ctrl.abort();
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-44 pb-32 text-center container-x">
        <p className="font-serif text-3xl text-[#0A192F]">Article not found</p>
        <Link to="/insights" className="btn-ghost-dark mt-10 inline-flex">Back to Insights</Link>
      </div>
    );
  }
  if (!post) {
    // Branded skeleton placeholder — no "Loading…" copy, fades in seamlessly
    return (
      <div data-testid="insight-detail-skeleton" className="bg-[#0A192F] text-white pt-44 pb-32">
        <div className="container-x max-w-[1000px]">
          <div className="h-3 w-32 bg-white/10 animate-pulse" />
          <div className="mt-10 h-16 w-3/4 bg-white/10 animate-pulse" />
          <div className="mt-4 h-16 w-2/3 bg-white/10 animate-pulse" />
          <div className="mt-12 h-3 w-48 bg-white/10 animate-pulse" />
        </div>
      </div>
    );
  }

  const cats = (post.categories || []).slice(0, 4);

  return (
    <div data-testid="insight-detail-page">
      <section className="bg-[#0A192F] text-white pt-40 pb-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-[#1B3563]/40 blur-[140px]" aria-hidden />
        <div className="container-x relative max-w-[1000px]">
          <Link to="/insights" data-testid="back-to-insights" className="overline text-[#C5A059] inline-flex items-center gap-2"><ArrowLeft size={14} /> Knowledge Center</Link>
          <div className="mt-10 flex flex-wrap gap-2">
            {cats.map((c) => <span key={c} className="px-3 py-1 border border-white/15 text-white/65 text-[10px] tracking-[0.22em] uppercase">{c}</span>)}
          </div>
          <h1 data-testid="insight-detail-title" className="editorial-display text-white text-[clamp(2.4rem,5.6vw,6rem)] mt-8 max-w-[24ch] leading-[0.98]">{post.title}</h1>
          <div className="mt-10 flex items-center gap-7 text-white/65 text-sm flex-wrap">
            <span className="inline-flex items-center gap-2"><Calendar size={14} /> {fmtDate(post.published)}</span>
            <span className="inline-flex items-center gap-2"><Clock size={14} /> {post.read_time}</span>
            {post.link && (
              <a href={post.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#C5A059] hover:underline">
                <ExternalLink size={14} /> View on blog.nrk.info
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-20 max-w-[860px]">
          {post.image && (
            <div className="overflow-hidden aspect-[16/9] bg-[#FBFAF6]">
              <SafeImage src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <article
            className="mt-16 prose-editorial text-[#1f2937] text-[1.08rem] leading-[1.95]"
            dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          />

          <div className="mt-20 flex flex-wrap items-center gap-4 justify-between">
            <Link to="/insights" data-testid="insight-back-link" className="btn-ghost-dark"><ArrowLeft size={14} /> All Insights</Link>
            {post.link && <a href={post.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0A192F] link-underline"><ExternalLink size={14} className="text-[#C5A059]" /> Read original on blog.nrk.info</a>}
          </div>
        </div>
      </section>
    </div>
  );
}
